import React, { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  layoutProcessDefinition,
  applyExecutionStatus,
  applyEdgeStatus,
  computePhaseGroups,
} from './abLayoutEngine';
import './ABProcessMap.css';

/* ---------- custom node component ---------- */
function ABExecutionNode({ data }) {
  const { typeLetter, label, status, isGate, executionType } = data;

  const statusClass = `ab-node-status-${status}`;
  let typeClass = 'ab-node-llm';
  if (isGate) typeClass = 'ab-node-gate';
  else if (executionType === 'snn_pipeline') typeClass = 'ab-node-snn';
  else if (executionType === 'case_extractor') typeClass = 'ab-node-case-extractor';
  else if (executionType === 'chain_reasoner') typeClass = 'ab-node-chain-reasoner';

  return (
    <div className={`ab-exec-node ${statusClass} ${typeClass}`} title={`${label}\n[${status}]`}>
      <Handle type="target" position={Position.Left} style={{ width: 6, height: 6, background: '#94a3b8', border: 'none' }} />
      <span className="ab-node-badge">{typeLetter}</span>
      <span className="ab-node-label">{label}</span>
      {status === 'completed' && <span className="ab-node-tick">&#10003;</span>}
      <Handle type="source" position={Position.Right} style={{ width: 6, height: 6, background: '#94a3b8', border: 'none' }} />
    </div>
  );
}

const nodeTypes = { abExecutionNode: ABExecutionNode };

export default function ABProcessMap({ definition, executionState, onNodeClick }) {
  const { layoutNodes, layoutEdges, phaseGroups } = useMemo(() => {
    const { nodes: ln, edges: le } = layoutProcessDefinition(definition);
    const pg = computePhaseGroups(definition, ln);
    return { layoutNodes: ln, layoutEdges: le, phaseGroups: pg };
  }, [definition]);

  const nodesWithStatus = useMemo(
    () => applyExecutionStatus(layoutNodes, executionState),
    [layoutNodes, executionState],
  );

  const edgesWithStatus = useMemo(
    () => applyEdgeStatus(layoutEdges, nodesWithStatus),
    [layoutEdges, nodesWithStatus],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(nodesWithStatus);
  const [edges, setEdges, onEdgesChange] = useEdgesState(edgesWithStatus);

  React.useEffect(() => {
    setNodes(nodesWithStatus);
  }, [nodesWithStatus, setNodes]);

  React.useEffect(() => {
    setEdges(edgesWithStatus);
  }, [edgesWithStatus, setEdges]);

  const handleNodeClick = useCallback(
    (_event, node) => {
      if (node.data.isGate) return;
      onNodeClick?.(node.id, node.data);
    },
    [onNodeClick],
  );

  return (
    <div className="ab-process-map-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        fitView
        fitViewOptions={{ padding: 0.15, maxZoom: 1.5 }}
        minZoom={0.1}
        maxZoom={4}
        panOnScroll
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e2e8f0" gap={24} variant="dots" />
        <Controls
          showInteractive={false}
          style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}
        />
        <MiniMap
          nodeColor={(n) => {
            const s = n.data?.status;
            if (s === 'completed') return '#16a34a';
            if (s === 'active') return '#22c55e';
            if (s === 'failed') return '#ef4444';
            if (s === 'skipped') return '#9ca3af';
            if (n.data?.executionType === 'snn_pipeline') return '#0284c7';
            if (n.data?.executionType === 'llm') return '#7c3aed';
            if (n.data?.executionType === 'case_extractor') return '#6366f1';
            if (n.data?.executionType === 'chain_reasoner') return '#f59e0b';
            return '#d97706';
          }}
          maskColor="rgba(248,250,252,0.7)"
          style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8 }}
        />

        {phaseGroups.map((pg) => (
          <div
            key={pg.label}
            className="ab-phase-group"
            style={{
              position: 'absolute',
              left: pg.bounds.x,
              top: pg.bounds.y,
              width: pg.bounds.width,
              height: pg.bounds.height,
              pointerEvents: 'none',
            }}
          >
            <span className="phase-label">{pg.label}</span>
          </div>
        ))}
      </ReactFlow>
    </div>
  );
}
